import { useEffect } from 'react';

export const PALETTE = {
  BRAND_BROWN: '#826845',
  BRAND_GREEN: '#618E69',
  BRAND_TEAL: '#5D9C9D',
  DEEP_FOREST: '#03605C',
  DEEP_FOREST_DK: '#024442',
  FOREST_SHADOW: '#013532',
  CREAM: '#F8F3EB',
  CREAM_SOFT: '#F1E7CE',
  INK: '#655F59',
  MEADOW_GOLD: '#B5882D',
  GOLD_LINE: '#B5882D',
  SEAL_TERRACOTTA: '#D76427',
  OCHRE: '#A56650',
  PALE_SAGE: '#DBD3A8',
  ROSE: '#B6787F',
  BURGUNDY: '#95373A',
};

export function WaveDivider({ height = 80, palette, flip = false }) {
  const layers = palette || [PALETTE.CREAM, PALETTE.CREAM_SOFT];
  const style = {
    transform: flip ? 'scaleY(-1)' : 'none',
    display: 'block',
    width: '100%',
    height,
  };
  return (
    <svg viewBox="0 0 1440 100" preserveAspectRatio="none" style={style} aria-hidden="true">
      <path
        d="M0,32 C220,4 460,72 720,36 C980,0 1220,60 1440,28 L1440,100 L0,100 Z"
        fill={layers[0]}
        opacity="0.95"
      />
      {layers[1] && (
        <path
          d="M0,52 C260,28 480,80 780,52 C1060,26 1260,72 1440,48 L1440,100 L0,100 Z"
          fill={layers[1]}
          opacity="0.65"
        />
      )}
      {layers[2] && (
        <path
          d="M0,72 C300,54 520,90 800,72 C1080,54 1260,88 1440,72 L1440,100 L0,100 Z"
          fill={layers[2]}
          opacity="0.4"
        />
      )}
      <path
        d="M0,34 C220,6 460,74 720,38 C980,2 1220,62 1440,30"
        stroke={PALETTE.GOLD_LINE}
        strokeWidth="1.1"
        fill="none"
        opacity="0.45"
      />
    </svg>
  );
}

export function WaveRule({ width = 84, stroke = PALETTE.MEADOW_GOLD }) {
  return (
    <svg
      width={width}
      height="6"
      viewBox="0 0 84 6"
      preserveAspectRatio="none"
      aria-hidden="true"
      style={{ display: 'block' }}
    >
      <path
        d="M0,3 C18,0 38,6 56,3 C72,0 80,4 84,3"
        stroke={stroke}
        strokeWidth="1.8"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function CardWaveCut({ fill = PALETTE.CREAM_SOFT }) {
  return (
    <svg
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: -1,
        width: '100%',
        height: 32,
        zIndex: 3,
        pointerEvents: 'none',
      }}
      viewBox="0 0 400 40"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path
        d="M0,20 C80,4 160,32 220,18 C280,4 340,28 400,14 L400,40 L0,40 Z"
        fill={fill}
      />
      <path
        d="M0,22 C80,6 160,34 220,20 C280,6 340,30 400,16"
        stroke={PALETTE.GOLD_LINE}
        strokeWidth="1"
        fill="none"
        opacity="0.55"
      />
    </svg>
  );
}

export function IconDisc({ tone = PALETTE.BRAND_GREEN, size = 68, children }) {
  return (
    <svg viewBox="0 0 68 68" width={size} height={size} aria-hidden="true">
      <circle cx="34" cy="34" r="33" fill={tone} />
      <g
        stroke={PALETTE.CREAM}
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {children}
      </g>
    </svg>
  );
}

export function useReveal(deps = []) {
  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const targets = document.querySelectorAll('.wtn-reveal, .wtn-reveal-group');
    if (reduced || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-inview'));
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-inview');
            io.unobserve(e.target);
          }
        }),
      { rootMargin: '0px 0px -10% 0px', threshold: 0.12 }
    );
    targets.forEach((el) => io.observe(el));
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
